import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LogIn, Eye, EyeOff } from 'lucide-react';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { Login } from './loginType';
import LoginApi from './loginApi';
import { useAuth } from '../components/common/authContext';
import { getSessionData } from '../lib/global_function';
import { Button } from '../components/ui/button';
import { Alert } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import CTASection from '../home/sections/CTA';

const LoginPage: React.FC = () => {
  const [message, setMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const { setIsAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [authProvider, setAuthProvider] = useState<string>('');
  const [errorCode, setErrorCode] = useState<string>('');

  const [login, setLoginData] = useState<Login>({
    login_id: '',
    login_password: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLoginData(prevData => ({ ...prevData, [name]: value }));
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsAuthenticated(false);
    setErrorMessage('');
    setAuthProvider("self");

    try {
      const response = await LoginApi.login(login);
      await handleAuthenticationSuccess(response);
    } catch (error: any) {
      setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
    }
  };

  const handleGoogleSuccess = async (response: any) => {
    setIsAuthenticated(false);
    setErrorMessage('');
    setAuthProvider('google');
    setErrorCode('');

    const sessionData = getSessionData(process.env.REACT_APP_SESSION_STORAGE_KEY as string);
    const companyCode = sessionData?.company_code || '';

 
    const token = response.credential;
    try {
      const apiResponse = await LoginApi.login_google(response.credential, companyCode);
      await handleAuthenticationSuccess(apiResponse);

    } catch (error: any) {
      let errorMessage = 'An unknown error occurred.';
      if (error instanceof Error) {
        if (error.message.includes('Code: x-404')) {
          errorMessage = 'Your account not match to company or not found. Please contact us first.';
          setErrorCode('x-404')
        } else {
          errorMessage = error.message;
        }
      }

      setErrorMessage(errorMessage);
    }
  };

  const handleGoogleFailure = () => {
    setErrorMessage('Sign in with Google failed');
  };

  const handleAuthenticationSuccess = async (response: any) => {
    const accountData = {
      account_id: response.data.attributes.account_id,
      first_name: response.data.attributes.first_name,
      last_name: response.data.attributes.last_name,
      company_code: response.data.attributes.company_code,
      company_name: response.data.attributes.company_name,
      subscription_status: response.data.attributes.subscription_status
    };

    //console.log("subscription_status====", response.data.attributes.subscription_status)

    const encodedMrjoeData = btoa(JSON.stringify(accountData));
    sessionStorage.setItem(process.env.REACT_APP_SESSION_STORAGE_KEY as string, encodedMrjoeData);
    setIsAuthenticated(true);
    navigate('/main/customer');

    {/**
    if (accountData.subscription_status === 'active') {
      navigate('/main/customer');
    } else {
      alert('There is NO active subscription available. You will be redirected to your account subscription page where you can start a new subscription');
      navigate('/main/account/subscription');
    }
       */}
  };

  return (
    <>
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_AUTH_CLIENT_ID || ''}>
        <section className="pt-8 md:pt-16 pb-8">
          <div className="container-safe">
            <div className="mx-auto max-w-2xl">
              <h1 className="page-heading mb-8">Log In</h1>

              {/* Error Message */}
              {errorMessage && (
                <Alert variant="error">
                  <div className="space-y-3">
                    <p className="font-semibold">{errorMessage}</p>

                    {errorCode === 'x-404' && (
                      <p className="text-sm">
                        To create a company, click{' '}
                        <a 
                          href="https://www.mrjoe.pro/create_company"
                          rel="noopener noreferrer"
                          className="text-primary underline hover:text-primary-hover font-medium"
                        >
                          here
                        </a>
                      </p>
                    )}

                    {errorCode !== 'x-404' && (
                      <div className="text-sm space-y-2">
                        {authProvider === "self" && (
                          <p>Login failed.</p>
                        )}
                        {authProvider === "google" && (
                          <p>Your Google Sign-In failed.</p>
                        )}
                
                        <p>
                          If you still have troubles to login, please click the "Contact Us" link at the bottom of this page to contact us.
                        </p>
                      </div>
                    )}
                  </div>
                </Alert>
              )}

              {/* Info Message */}
              {message && (
                <Alert variant="info">
                  {message}
                </Alert>
              )}

              <div className="space-y-6">
                {/* Own Account Login */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h2 className="form-section-heading">Your Own Account Login</h2>
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                      <Label htmlFor="login_id">
                        Login ID <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        type="text"
                        id="login_id"
                        name="login_id"
                        value={login.login_id || ''}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="login_password">
                        Password <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          id="login_password"
                          name="login_password"
                          value={login.login_password || ''}
                          onChange={handleInputChange}
                          required
                          className="pr-12"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showPassword ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <Button type="submit" size="medium" variant="default">
                        <LogIn className="size-5" />
                        Log In
                      </Button> &nbsp;&nbsp;
                    
                      <Link
                        to='/forget_password'
                        className="text-primary text-base hover:text-primary-hover underline"
                      >
                        Forget Password?
                      </Link>
                    </div>
                  </form>
                </div>

                {/* Google Login */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h2 className="form-section-heading">Your Google Account Login</h2>
                  <div className="flex justify-center">
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={handleGoogleFailure}
                      theme="filled_blue"
                      width="300px"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </GoogleOAuthProvider>
      <CTASection />
    </>
  );
};

export default LoginPage;